import type { ReactNode } from "react";
import { useLocale, useTranslations } from "use-intl";

import { Card, CardContent, CardHeader } from "@lawline/ui/components/card";

import { CopyButton } from "@platform/components/ui/copy-button";
import { ExternalLink } from "@platform/components/ui/external-link";
import { docsLink } from "@platform/core/docs";

const TAGS = {
	b: (chunks: ReactNode) => <strong className="font-medium">{chunks}</strong>,
	code: (chunks: ReactNode) => (
		<code className="bg-muted rounded px-1 py-0.5 font-mono text-xs wrap-anywhere">{chunks}</code>
	),
};

function Snippet({ code, copyLabel }: { code: string; copyLabel: string }) {
	return (
		<div className="flex flex-col gap-2">
			<pre className="bg-muted overflow-x-auto rounded-md border p-3 font-mono text-xs leading-relaxed">
				<code>{code}</code>
			</pre>
			<div className="flex justify-end">
				<CopyButton value={code} label={copyLabel} />
			</div>
		</div>
	);
}

function App({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section className="flex flex-col gap-2">
			<h3 className="font-medium">{title}</h3>
			{children}
		</section>
	);
}

// The same steps as the docs guide for MCP clients, with this environment's URL filled in.
export function ConnectSteps({ url }: { url: string }) {
	const t = useTranslations("aiApps.steps");
	const locale = useLocale();
	const cursorEntry = JSON.stringify({ mcpServers: { lawline: { url } } }, null, 2);
	const claudeCodeCommand = `claude mcp add --transport http lawline ${url}`;

	return (
		<Card>
			<CardHeader>
				<h2 className="text-base font-medium">{t("title")}</h2>
			</CardHeader>
			<CardContent className="flex flex-col gap-6">
				<App title={t("claude.title")}>
					<ol className="flex list-decimal flex-col gap-1 pl-5">
						<li>{t.rich("claude.openSettings", TAGS)}</li>
						<li>{t.rich("claude.enterUrl", { ...TAGS, url })}</li>
						<li>{t.rich("claude.connect", TAGS)}</li>
					</ol>
				</App>

				<App title={t("chatgpt.title")}>
					<p className="text-muted-foreground">{t("chatgpt.plan")}</p>
					<ol className="flex list-decimal flex-col gap-1 pl-5">
						<li>{t.rich("chatgpt.addConnector", { ...TAGS, url })}</li>
						<li>{t("chatgpt.connect")}</li>
					</ol>
				</App>

				<App title={t("cursor.title")}>
					<ol className="flex list-decimal flex-col gap-2 pl-5">
						<li>
							<div className="flex flex-col gap-2">
								<span>{t.rich("cursor.addEntry", TAGS)}</span>
								<Snippet code={cursorEntry} copyLabel={t("cursor.copy")} />
							</div>
						</li>
						<li>{t("cursor.connect")}</li>
					</ol>
				</App>

				<App title={t("claudeCode.title")}>
					<ol className="flex list-decimal flex-col gap-2 pl-5">
						<li>
							<div className="flex flex-col gap-2">
								<span>{t("claudeCode.run")}</span>
								<Snippet code={claudeCodeCommand} copyLabel={t("claudeCode.copy")} />
							</div>
						</li>
						<li>{t.rich("claudeCode.connect", TAGS)}</li>
					</ol>
				</App>

				<p className="text-muted-foreground">
					{t.rich("details", {
						docs: (chunks) => (
							<ExternalLink href={docsLink("connectClient", locale)}>{chunks}</ExternalLink>
						),
					})}
				</p>
			</CardContent>
		</Card>
	);
}
